import type { Entries } from './catalog';
import { operator } from './operator';

/**
 * The English words of Snapshots. The object is a "snapshot" everywhere a person reads (decision E9); a
 * count's words are `<key>.one` and `<key>.other`, chosen by `Catalog.count`. The shared components'
 * words come from `@beyond-js/ui` (`Copies`).
 */
export const en: Entries = {
	'app.name': 'Beyond Snapshots',
	'app.product': 'Snapshots',
	'app.tagline': 'Immutable snapshots of a project’s packages.',
	// What a loading line names, in `Loading.text` (@beyond-js/ui 0.7.6): "Reading who is here…"
	'loading.who': 'who is here',
	'loading.organizations': 'your organizations',
	'loading.capture': 'the snapshot',
	'loading.storage': 'what this organization stores',
	'loading.files': 'its files',
	'loading.file': 'the file',
	'loading.compare': 'the two snapshots',
	'state.departing': 'Signing you in with Beyond Accounts…',
	'state.unavailable.reason': 'Nothing about your snapshots has changed.',
	'action.retry': 'Try again',
	'action.signin': 'Sign in with Beyond Accounts',
	'action.signout': 'Sign out',
	'action.back': 'Back to snapshots',
	'action.delete': 'Delete snapshot',
	'action.restore': 'Restore snapshot',
	'action.rename': 'Save title',
	'action.cancel': 'Cancel',
	'nav.captures': 'Snapshots',
	'nav.storage': 'Storage',
	'nav.notifications': 'Notifications',
	'nav.project': 'Project',
	'nav.organization': 'Organization',
	'nav.project.unknown': 'Unknown project',
	'page.list': 'Snapshots · Beyond Snapshots',
	'page.capture': '{title} · {state} · Beyond Snapshots',
	'page.storage': 'Storage · Beyond Snapshots',
	'page.compare': 'Compare snapshots · Beyond Snapshots',
	'signin.title': 'Sign in to see snapshots',
	'signin.body': 'Sign in with Beyond Accounts to open your organizations’ snapshots.',
	'signin.expired': 'Your session expired. Sign in again to continue where you were.',
	'signin.revoked': 'Your session was ended: you signed out of Beyond, or it was ended from Beyond Accounts. Sign in again to continue where you were.',
	'signin.beyond': 'You signed out of Beyond in this browser.',
	'signin.signedout': 'You signed out of Snapshots.',
	'signin.unconfirmed': 'You signed out here, but Beyond Accounts did not confirm that it ended the session. It ends by itself when it expires.',
	'signin.failed': 'That sign-in could not be completed. Start it again.',
	'signin.unavailable': 'Beyond Accounts could not be reached, so nobody was signed in. Try again in a moment.',
	'signin.incomplete': 'The last sign-in did not finish, so you were not sent to Beyond Accounts again. Start it when you are ready.',
	'signout.failed': 'Signing out was not confirmed, so you are still signed in. Try again.',
	'suspended.title': 'This account cannot use Beyond',
	'suspended.lead': 'Your Beyond account is suspended, so signing in again will not help. Contact whoever administers your account.',
	'unsaved.title': 'Sign out with an unsaved title?',
	'unsaved.message': 'The title you typed for “{title}” is not saved yet.',
	'unsaved.lost': 'The title you typed.',
	'unsaved.kept': 'The snapshot and its saved title.',
	'unsaved.accept': 'Sign out of Beyond',
	'unsaved.keep': 'Keep editing',
	'family.signout': 'Sign out',
	'notifications.title': 'Notifications',

	// The list
	'captures.title': 'Snapshots',
	'captures.untitled': 'Untitled snapshot',
	'title.more': '{name} and {count} more',
	'title.made': '{packages} · {time}',
	'captures.scope.all': 'Every snapshot of your organizations.',
	'captures.scope.project': 'In the project {project}.',
	'captures.scope.organization': 'In {organization}.',
	'captures.make': 'Make a snapshot in Packages',
	'captures.column.title': 'Snapshot',
	'captures.column.project': 'Project',
	'captures.column.organization': 'Organization',
	'captures.column.author': 'Made by',
	'captures.column.source': 'Source',
	'captures.column.packages': 'Packages',
	'captures.column.size': 'Size',
	'captures.column.created': 'Created',
	'captures.column.state': 'State',
	'captures.column.deleted': 'Deleted',
	'captures.column.remover': 'Deleted by',
	'captures.column.recoverable': 'Recoverable until',
	'captures.filter.state': 'State',
	'captures.filter.current': 'All but deleted',
	'captures.state.pending': 'Pending',
	'captures.state.active': 'Ready',
	'captures.state.deleted': 'Deleted',
	'captures.state.ended': 'Failed or cancelled',
	'captures.state.cancelled': 'Cancelled',
	'captures.state.failed': 'Failed',
	'captures.empty.title': 'No snapshots yet',
	'captures.empty.body': 'Make a snapshot of your project’s packages in Packages.',
	'captures.deleted': 'Show deleted snapshots',
	'quota.near': 'This organization keeps {used} of its {quota} snapshots.',
	'quota.full': 'This organization reached its quota: delete snapshots to make room for new ones.',
	'quota.storage': 'This organization’s snapshots hold {used} of its {quota}.',
	'quota.see': 'See storage',
	'source.main': 'Main',
	'source.conversation': 'A conversation',
	'source.local': 'This computer',
	'source.external': 'Another server',
	'source.conduict': 'Conduict',
	'person.you': 'You',
	'person.member': 'A member of {organization}',
	'person.someone': 'A member of the organization',
	'list.search': 'Search snapshots',
	'list.placeholder': 'Title, package, identity or digest',
	'list.all': 'All',
	'list.loading': 'Loading snapshots…',
	'list.failure': 'The snapshots could not be loaded.',
	'list.pages': '{label} pages',
	'list.matches': 'No snapshots match “{query}”.',
	'list.filtered': 'No snapshots match this filter.',
	'list.end.one': '{count} snapshot · nothing older',
	'list.end.other': '{count} snapshots · nothing older',
	'list.removed': 'Deleted by {person}',

	// Refusals in place
	'unavailable.member.title': 'You are not in an organization yet',
	'unavailable.member.reason': 'Join an organization to read its projects’ snapshots.',
	'unavailable.member.owner': 'An owner of an organization, by inviting you; or you, by creating one in Beyond Accounts',
	'unavailable.member.action': 'Create an organization',
	'unavailable.member.projects': 'Open Beyond Projects',
	'unavailable.capture.title': 'This snapshot is not available to you',
	'unavailable.capture.reason': 'It does not exist, or it belongs to an organization you are not in.',
	'unavailable.capture.owner': 'An owner or admin of the organization that holds it, by adding you there',
	'unavailable.project.title': 'This project’s snapshots are not available to you',
	'unavailable.project.reason': 'The project does not exist, it was deleted, or it belongs to an organization you are not in.',
	'unavailable.project.owner': 'An owner or admin of the project’s organization',
	'unavailable.organization.title': 'This organization’s snapshots are not available to you',
	'unavailable.organization.reason': 'The organization does not exist, or you are not a member of it.',
	'unavailable.organization.owner': 'An owner or admin of the organization, by adding you there',
	'unavailable.empty.title': 'No snapshots of this project yet',
	'unavailable.empty.reason': 'Make this project’s snapshots in Packages. Everyone with project access can read them.',
	'unavailable.empty.owner': 'A developer, admin or owner of the project’s organization',
	'unavailable.empty.action': 'Make a snapshot in Packages',
	'unavailable.enable.title': 'Packages isn’t enabled for this project',
	'unavailable.enable.reason': 'Snapshots of this project are made in Packages, which is not enabled here yet.',
	'unavailable.enable.owner': 'An owner or admin of the project’s organization',
	'unavailable.enable.action': 'Enable Packages for this project',
	'first.title': 'Your first snapshot',
	'first.enable': 'Enable Packages for this project',
	'first.enable.owner': 'An owner or admin of the project’s organization enables it',
	'first.make': 'Make a snapshot in Packages',
	'first.publish': 'Publish it on CDN',
	'first.publish.detail': 'From the snapshot’s page, once it is complete',
	'unavailable.all': 'See all your snapshots',

	// One snapshot
	'capture.title': 'Snapshot',
	'capture.facts': 'About this snapshot',
	'capture.facts.created': 'Created {date}',
	'capture.identity': 'Identity',
	'capture.author': 'Made by',
	'capture.owner': 'Organization',
	'capture.created': 'Created',
	'capture.completed': 'Ready since',
	'capture.ended': 'Ended',
	'capture.remover': 'Deleted by',
	'capture.provenance': 'Source',
	'capture.provenance.main': 'Main',
	'capture.provenance.conversation': 'A conversation’s copy',
	'capture.provenance.local': 'This computer',
	'capture.provenance.external': 'Another server',
	'capture.provenance.conduict': 'Conduict',
	'capture.provenance.unknown': 'Not recorded',
	'capture.connection': 'Connection',
	'capture.connection.local': 'This computer',
	'capture.connection.external': 'Another server',
	'capture.connection.conduict': 'Conduict',
	'capture.toolchain': 'Toolchain',
	'capture.repository': 'Repository',
	'capture.made': 'Made through',
	'capture.used': 'Used by',
	'capture.used.none': 'Nothing uses it',
	'capture.use.release': 'A release in {product}',
	'capture.use.other': 'Something in {product}',
	'capture.same': 'Same content as',
	'capture.same.earlier': 'An earlier snapshot',
	'capture.packages': 'Packages',
	'capture.packages.count.one': '{count} package',
	'capture.packages.count.other': '{count} packages',
	'capture.package.path': 'Path',
	'capture.package.name': 'Name',
	'capture.package.version': 'Version',
	'capture.package.digest': 'Digest',
	'capture.package.bytes': 'Size',
	'capture.package.dependencies': 'Dependencies',
	'capture.package.dependencies.none': 'No dependencies',
	'capture.package.unstored': 'Not received yet',
	'capture.package.received': 'Received',
	'capture.files.folder': 'Folder',
	'capture.package.damaged': 'Its stored bytes no longer match their digest. Make the snapshot again in Packages.',
	'capture.download': 'Download',
	'capture.download.named': 'Download {name}',
	'capture.files': 'Files',
	'capture.files.show': 'Files of {name}',
	'capture.files.count.one': '{count} file',
	'capture.files.count.other': '{count} files',
	'capture.files.truncated': 'Only the first {count} entries are listed.',
	'capture.files.unreadable': 'Its files cannot be listed. Download it to read it.',
	'capture.files.empty': 'It holds no files.',
	'capture.file.binary': 'This file is not text. Download the package to open it.',
	'capture.file.truncated': 'Only its first 512 KB are shown.',
	'capture.file.close': 'Close',
	'capture.file.text': 'Text of {name}',
	'capture.configuration': 'Publication settings',
	'capture.configuration.hint': 'The project’s publication settings when the snapshot was made.',
	'capture.digest.help': 'The SHA-256 of this package’s archive: it names these exact bytes.',
	'capture.title.label': 'Title',
	'capture.publish': 'Publish on CDN',
	'capture.open': 'Open in Packages',
	'capture.compare': 'Compare',
	'capture.compare.previous': 'Compare with the previous one',
	'capture.compare.with': 'Compare with another snapshot of this project',
	'capture.compare.none': 'No other snapshot of this project',

	// Statuses: one vocabulary
	'capture.standing.pending': 'Pending',
	'capture.standing.completed': 'Ready',
	'capture.standing.failed': 'Failed',
	'capture.standing.cancelled': 'Cancelled',
	'capture.standing.deleted': 'Deleted, recoverable until {until}',

	// While its packages arrive, and how it ended
	'capture.awaited.title': 'Receiving this snapshot’s packages',
	'capture.awaited.complete': 'Complete the snapshot',
	'capture.awaited.sender.local': 'Packages on this computer is sending them. Closing Packages there stops the snapshot.',
	'capture.awaited.sender.external': 'Packages on another server is sending them. Stopping that server stops the snapshot.',
	'capture.awaited.sender.conduict': 'A Conduict machine is sending them. Stopping the machine stops the snapshot.',
	'capture.awaited.sender.workspace': 'A Workspace environment is sending them. Stopping it stops the snapshot.',
	'capture.awaited.sender.packages': 'Packages is sending them. Closing it stops the snapshot.',
	'capture.awaited.deadline': 'It fails at {time} unless every package has arrived.',
	'capture.awaited.stalled': '{missing} of its {total} packages never arrived in time, so this snapshot failed.',
	'capture.awaited.gone': 'This snapshot no longer exists.',
	'capture.awaited.again': 'Trying again here can’t help: make the snapshot again in Packages.',
	'capture.ended.cancelled': 'Cancelled {date} by {who}, before every package arrived.',
	'capture.ended.failed': '{missing} of its {total} packages never arrived in time, so it failed {date}.',
	'capture.ended.kept': 'Its record is removed {until}. Make the snapshot again in Packages.',

	// Lifecycle
	'capture.more': 'More actions for {title}',
	'capture.cancel.item': 'Cancel {title}…',
	'capture.cancel.question': 'Cancel “{title}”?',
	'capture.cancel.message': 'Snapshots stops receiving its packages.',
	'capture.cancel.lost': 'The packages received so far',
	'capture.cancel.kept': 'A record of it, until {until}',
	'capture.cancel.kept.period': 'A record of it, for a few days',
	'capture.cancel.recovery': 'None: make the snapshot again in Packages',
	'capture.cancel.confirm': 'Cancel snapshot',
	'capture.cancel.keep': 'Keep receiving',
	'capture.cancelled.outcome': 'Snapshot cancelled.',
	'capture.delete.item': 'Delete {title}…',
	'capture.delete.question': 'Delete “{title}”?',
	'capture.delete.message': 'This snapshot disappears from the project. Its packages can no longer be read or downloaded.',
	'capture.delete.lost': 'Reading and downloading its packages, for everyone',
	'capture.delete.lost.held': 'Reading and downloading its packages; and {releases} can no longer be promoted or rolled back to it',
	'capture.delete.kept': 'Releases that use it keep serving what they published; it can be restored until {until}',
	'capture.delete.kept.period': 'Releases that use it keep serving what they published; it can be restored for a period',
	'capture.delete.recovery': 'An owner or admin can restore it until the recovery period ends. Then it is permanently removed.',
	'capture.delete.recovery.held': 'An owner or admin can restore it while recoverable. Releases keep it stored while needed.',
	'capture.delete.confirm': 'Delete snapshot',
	'capture.delete.keep': 'Keep snapshot',
	'capture.deleted.outcome': 'Snapshot deleted. It can be restored until {until}.',
	'capture.restored.outcome': 'Snapshot restored.',
	'capture.retitled.outcome': 'Title saved.',
	'reason.delete': 'Owners and admins delete snapshots',
	'reason.cancel': 'The person who started it, or an owner or admin, cancels it',
	'reason.rename': 'Developers, admins and owners rename snapshots',

	// Failures: one sentence per cause, with its way on
	'problem.unknown': 'The change may have been made. Check before trying again.',
	'problem.unchanged': 'Nothing was changed.',
	'problem.check': 'Check the snapshot',
	'problem.forbidden': 'Your role does not allow this here, so nothing changed. Read the snapshot again to see what you can do.',
	'problem.conflict': 'It changed in the meantime, so nothing was changed. Read it again and retry.',
	'problem.missing': 'It no longer exists, or it is not yours to read.',
	'problem.admission': 'Your role does not make snapshots in this project, so nothing changed.',
	'problem.admission.enable': 'Packages isn’t enabled for this project, so nothing changed. It is enabled in Beyond Projects.',
	'problem.state.active': 'It is already completed, so nothing changed.',
	'problem.state.deleted': 'It was deleted, so nothing changed. Restore it first.',
	'problem.state.cancelled': 'It was cancelled, so nothing changed. Make the snapshot again in Packages.',
	'problem.state.failed': 'It failed, so nothing changed. Make the snapshot again in Packages.',
	'problem.state.expired': 'Its time to complete passed, so nothing changed. Make the snapshot again in Packages.',
	'problem.incomplete': 'It is not complete yet, so its packages cannot be read.',
	'problem.invalid': 'Snapshots did not accept what was sent, so nothing changed. Check it and try again.',
	'problem.large': 'That is larger than Snapshots accepts, so nothing changed.',
	'problem.limit.snapshots': 'This organization keeps at most {limit} snapshots, so nothing changed. Delete some to make room.',
	'problem.limit.storage': 'This organization’s snapshots hold at most {limit}, so nothing changed. Delete some to make room.',
	'problem.limit.captures': 'You have {limit} snapshots pending, so nothing changed. Wait for one to finish, or cancel one.',
	'problem.limit.bytes': 'This organization’s pending snapshots hold at most {limit}, so nothing changed. Wait for one to finish.',
	'problem.limit.busy': 'Too much was asked at once, so nothing changed. Wait a minute and try again.',
	'problem.refused': 'Snapshots did not accept this, so nothing changed. Read the page again before trying again.',

	// An outage said once (D163): who did not answer, then each read it holds as its Technical details name it
	'outage.cause': '{name} did not answer',
	'outage.storage': 'Beyond Snapshots’ storage did not answer',
	'outage.read.list': 'The list of snapshots',
	'outage.read.storage': 'What this organization stores',
	'outage.read.capture': 'This snapshot',
	'outage.read.before': 'The snapshot before',
	'outage.read.after': 'The snapshot after',
	'outage.read.file': 'The file {name}',

	// Storage
	'storage.unnamed': 'Unnamed organization',
	'storage.choose': 'Choose an organization',
	'storage.title': 'Storage',
	'storage.scope': 'In {organization}.',
	'storage.snapshots': 'Snapshots kept',
	'storage.bytes': 'Stored',
	'storage.meter.snapshots': '{used} of {quota}',
	'storage.meter.bytes': '{used} of {quota}',
	'storage.deleted.one': '{count} deleted snapshot still holds {bytes} until it is removed.',
	'storage.deleted.other': '{count} deleted snapshots still hold {bytes} until they are removed.',
	'storage.projects': 'By project',
	'storage.column.project': 'Project',
	'storage.column.snapshots': 'Snapshots',
	'storage.column.bytes': 'Stored',
	'storage.empty': 'This organization keeps no snapshots yet.',
	'storage.search': 'Search projects',
	'storage.checked': 'Counted {time}',
	'storage.same': 'The same bytes are stored once, however many snapshots hold them.',

	// Comparing two snapshots
	'compare.title': 'Compare snapshots',
	'compare.before': 'Before',
	'compare.after': 'After',
	'compare.same': 'Same content: every package holds the same bytes.',
	'compare.change.added': 'Added',
	'compare.change.removed': 'Removed',
	'compare.change.version': 'Version changed',
	'compare.change.content': 'Content changed',
	'compare.change.same': 'Same',
	'compare.version': '{before} → {after}',
	'compare.files.show': 'Files that changed in {name}',
	'compare.files.none': 'No file changed.',
	'compare.files.count.one': '{count} file changed',
	'compare.files.count.other': '{count} files changed',
	'compare.files.unreadable': 'Its files cannot be compared.',
	'compare.file.added': 'Added',
	'compare.file.removed': 'Removed',
	'compare.file.changed': 'Changed',
	'compare.missing': 'Both snapshots must be ones you can read.',
	// The cloud packages allowances, for Beyond operators (`operator.ts`)
	...operator.en
};
